import * as React from "react"
import * as TooltipPrimitive from "@radix-ui/react-tooltip"
import { useReducedMotion } from "motion/react"

import { cn } from "../../lib/utils"

type TooltipSkipDelayContextValue = {
  skipDelay: boolean
  skipDelayActive: boolean
  skipDelayActiveRef: React.MutableRefObject<boolean>
  markOpened: () => void
  markClosed: () => void
}

const TooltipSkipDelayContext =
  React.createContext<TooltipSkipDelayContextValue | null>(null)

type TooltipProviderProps = React.ComponentProps<
  typeof TooltipPrimitive.Provider
> & {
  /** When true, hopping between triggers under this provider skips delay and enter/exit animation. */
  skipDelay?: boolean
}

function TooltipProvider({
  delayDuration = 0,
  skipDelayDuration = 300,
  skipDelay = false,
  children,
  ...props
}: TooltipProviderProps) {
  const [skipDelayActive, setSkipDelayActive] = React.useState(false)
  const skipDelayActiveRef = React.useRef(false)
  const skipDelayTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(
    null
  )

  const clearSkipDelayTimer = React.useCallback(() => {
    if (skipDelayTimerRef.current) {
      clearTimeout(skipDelayTimerRef.current)
      skipDelayTimerRef.current = null
    }
  }, [])

  const markOpened = React.useCallback(() => {
    clearSkipDelayTimer()
  }, [clearSkipDelayTimer])

  const markClosed = React.useCallback(() => {
    clearSkipDelayTimer()
    skipDelayActiveRef.current = true
    setSkipDelayActive(true)
    skipDelayTimerRef.current = setTimeout(() => {
      skipDelayActiveRef.current = false
      setSkipDelayActive(false)
    }, skipDelayDuration)
  }, [clearSkipDelayTimer, skipDelayDuration])

  React.useEffect(() => () => clearSkipDelayTimer(), [clearSkipDelayTimer])

  const value = React.useMemo(
    () => ({
      skipDelay,
      skipDelayActive,
      skipDelayActiveRef,
      markOpened,
      markClosed,
    }),
    [skipDelay, skipDelayActive, markOpened, markClosed]
  )

  return (
    <TooltipSkipDelayContext.Provider value={value}>
      <TooltipPrimitive.Provider
        data-slot="tooltip-provider"
        delayDuration={delayDuration}
        skipDelayDuration={skipDelayDuration}
        {...props}
      >
        {children}
      </TooltipPrimitive.Provider>
    </TooltipSkipDelayContext.Provider>
  )
}

const TooltipOpenContext = React.createContext<boolean | null>(null)

function TooltipRoot({
  open: openProp,
  defaultOpen,
  onOpenChange,
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Root>) {
  const ctx = React.useContext(TooltipSkipDelayContext)
  const [uncontrolledOpen, setUncontrolledOpen] = React.useState(
    defaultOpen ?? false
  )
  const isControlled = openProp !== undefined
  const open = isControlled ? openProp : uncontrolledOpen

  const handleOpenChange = React.useCallback(
    (nextOpen: boolean) => {
      if (ctx) {
        if (nextOpen) {
          ctx.markOpened()
        } else {
          ctx.markClosed()
        }
      }
      if (!isControlled) setUncontrolledOpen(nextOpen)
      onOpenChange?.(nextOpen)
    },
    [ctx, isControlled, onOpenChange]
  )

  return (
    <TooltipOpenContext.Provider value={open}>
      <TooltipPrimitive.Root
        data-slot="tooltip"
        open={open}
        onOpenChange={handleOpenChange}
        {...props}
      />
    </TooltipOpenContext.Provider>
  )
}

function Tooltip({
  skipDelay,
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Root> & {
  skipDelay?: boolean
}) {
  return (
    <TooltipProvider skipDelay={skipDelay}>
      <TooltipRoot {...props} />
    </TooltipProvider>
  )
}

function TooltipTrigger({
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Trigger>) {
  return <TooltipPrimitive.Trigger data-slot="tooltip-trigger" {...props} />
}

function TooltipContent({
  className,
  sideOffset = 0,
  children,
  hideArrow = false,
  multiline = false,
  skipDelay = false,
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Content> & {
  hideArrow?: boolean
  multiline?: boolean
} & {
  skipDelay?: boolean
}) {
  const reduceMotion = useReducedMotion() ?? false
  const open = React.useContext(TooltipOpenContext)
  const ctx = React.useContext(TooltipSkipDelayContext)
  const skipDelayEnabled = skipDelay || Boolean(ctx?.skipDelay)
  const isInstantHop =
    skipDelayEnabled &&
    Boolean(ctx?.skipDelayActiveRef.current || ctx?.skipDelayActive)
  const reduceMotionActive = reduceMotion || isInstantHop

  if (skipDelayEnabled && open === false) {
    return null
  }

  return (
    <TooltipPrimitive.Portal>
      <TooltipPrimitive.Content
        data-slot="tooltip-content"
        data-reduce-motion={reduceMotionActive ? "" : undefined}
        sideOffset={sideOffset}
        className={cn(
          "ui-bg-primary ui-animate-in ui-fade-in-0 ui-zoom-in-95 data-[state=closed]:ui-animate-out data-[state=closed]:ui-fade-out-0 data-[state=closed]:ui-zoom-out-95 data-[state=instant-open]:!animate-none data-[state=instant-open]:!fade-in-0 data-[state=instant-open]:!zoom-in-95 data-[side=bottom]:ui-slide-in-from-top-2 data-[side=left]:ui-slide-in-from-right-2 data-[side=right]:ui-slide-in-from-left-2 data-[side=top]:ui-slide-in-from-bottom-2 ui-z-[9999] origin-(--radix-tooltip-content-transform-origin) ui-rounded-md ui-px-3 ui-py-1.5 ui-text-text-xs-medium ui-text-zinc-50",
          multiline
            ? "ui-w-64 ui-min-h-10 ui-text-left ui-whitespace-normal ui-break-words ui-line-clamp-2 ui-text-balance ![--tw-enter-scale:1] ![--tw-exit-scale:1]"
            : "ui-w-fit ui-text-balance",
          className
        )}
        {...props}
      >
        {children}
        {!hideArrow && (
          <TooltipPrimitive.Arrow className="ui-bg-primary ui-fill-primary ui-z-[9999] ui-size-2.5 ui-translate-y-[calc(-50%_-_2px)] ui-rotate-45 ui-rounded-[2px]" />
        )}
      </TooltipPrimitive.Content>
    </TooltipPrimitive.Portal>
  )
}

export { Tooltip, TooltipRoot, TooltipTrigger, TooltipContent, TooltipProvider }
